export interface TooltipEntry {
  title: string;
  description: string;
  interpretation?: string;
}

export const tooltipContent: Record<string, TooltipEntry> = {
  // Risk Assessment
  riskAssessment: {
    title: 'Enhanced Risk Assessment',
    description: 'A composite score evaluating how risky this mortgage is based on down payment, LTV, seller credits, and cash reserves.',
    interpretation: 'Lower scores = less risk. Aim for under 40.',
  },
  riskScore: {
    title: 'Risk Score',
    description: 'A 0–100 score combining loan-to-value, cash-to-close ratio, and seller credit dependency.',
    interpretation: '0–30 = Low risk, 31–60 = Medium, 61–80 = High, 81+ = Critical.',
  },
  loanToValue: {
    title: 'Loan-to-Value (LTV)',
    description: 'The percentage of the home price financed by the loan. LTV = Loan Amount ÷ Home Price.',
    interpretation: 'Below 80% avoids PMI. Lower LTV = less lender risk and better rates.',
  },
  cashToCloseRatio: {
    title: 'Cash-to-Close Ratio',
    description: 'How much cash you need at closing relative to the home price (down payment + closing costs - seller credits).',
    interpretation: 'Higher ratios mean more upfront capital required.',
  },
  sellerCreditDependency: {
    title: 'Seller Credit Dependency',
    description: 'How much of your closing costs are covered by seller credits.',
    interpretation: 'High dependency (>50%) means the deal heavily relies on seller concessions, which can be risky if the seller renegotiates.',
  },
  sellerCreditEffectiveness: {
    title: 'Seller Credit Effectiveness',
    description: 'How effectively the seller credit reduces your out-of-pocket costs at closing.',
    interpretation: '"Excellent" means the credit covers most closing costs. "Minimal" means it barely makes a dent.',
  },

  // Cost Analysis
  costAnalysis: {
    title: 'Cost Analysis',
    description: 'Breakdown of all costs associated with buying and financing this home over the full loan term.',
  },
  homePrice: {
    title: 'Home Price',
    description: 'The purchase price of the property before any credits or adjustments.',
  },
  totalInterest: {
    title: 'Total Interest',
    description: 'The total amount of interest you\'ll pay over the entire loan term. This is the "cost of borrowing."',
    interpretation: 'A lower percentage of the loan amount is better. Consider a shorter term or lower rate to reduce this.',
  },
  closingCosts: {
    title: 'Closing Costs',
    description: 'One-time fees paid at closing: lender fees, title insurance, appraisal, prepaid taxes/insurance, etc.',
    interpretation: 'Typically 2–5% of home price. Above 5% is unusually high — negotiate or shop around.',
  },
  totalCostOfOwnership: {
    title: 'Total Cost of Ownership',
    description: 'Home Price + Total Interest + Closing Costs. The true all-in cost of this home over the loan term.',
    interpretation: 'This is the number that matters most for long-term financial planning.',
  },

  // Income Requirements
  incomeRequirements: {
    title: 'Income Requirements',
    description: 'The minimum income needed so your housing payment stays within safe debt-to-income limits.',
  },
  recommendedIncome: {
    title: 'Recommended Annual Income',
    description: 'The annual gross income needed so your total monthly payment is no more than 28% of gross monthly income.',
    interpretation: 'If your actual income is below this, you may be "house poor" — too much income going to housing.',
  },
  dtiRule: {
    title: '28% DTI Rule',
    description: 'The front-end debt-to-income ratio guideline: housing costs should not exceed 28% of gross monthly income.',
    interpretation: 'This is a guideline, not a hard rule. Some loan programs allow up to 31% or higher.',
  },

  // Payment Composition
  paymentComposition: {
    title: 'Payment Composition Over Time',
    description: 'Shows how your monthly payment splits between principal (building equity) and interest (cost of borrowing).',
  },
  year1Principal: {
    title: 'Year 1 Principal',
    description: 'Total principal paid in the first 12 months. This is equity you\'re building in the home.',
    interpretation: 'Early in the loan, most of your payment goes to interest. This number grows each year.',
  },
  year1Interest: {
    title: 'Year 1 Interest',
    description: 'Total interest paid in the first 12 months. This is the "cost" of borrowing money.',
    interpretation: 'This is typically the highest interest year. It decreases over time as the balance drops.',
  },
  interestPrincipalRatio: {
    title: 'Interest vs Principal Ratio',
    description: 'Total interest divided by loan amount. Shows how much extra you pay beyond what you borrowed.',
    interpretation: 'A ratio of 0.50:1 means you pay 50¢ in interest for every $1 borrowed. Lower is better.',
  },

  // Key Insights
  keyInsights: {
    title: 'Key Insights',
    description: 'Quick status checks on the most important aspects of your mortgage.',
  },
  pmiStatus: {
    title: 'PMI Status',
    description: 'Private Mortgage Insurance is required when your down payment is less than 20% (LTV > 80%).',
    interpretation: 'PMI adds $50–$250/mo. It drops off once you reach 80% LTV through payments or appreciation.',
  },
  downPaymentInsight: {
    title: 'Down Payment',
    description: 'The percentage of the home price you\'re paying upfront.',
    interpretation: '20%+ is ideal (avoids PMI). 10–19% is common. Below 5% significantly increases costs.',
  },
  interestCostInsight: {
    title: 'Interest Cost',
    description: 'Whether your total interest is reasonable relative to the loan amount.',
    interpretation: 'Under 50% of loan amount is good. Over 100% means you\'re paying more in interest than you borrowed.',
  },
  closingCostsInsight: {
    title: 'Closing Costs',
    description: 'Whether your closing costs are within the normal range.',
    interpretation: 'Under 3% of home price is normal. 3–5% is acceptable. Over 5% warrants a closer look.',
  },

  // Buydown Tab
  buydownScenarios: {
    title: 'Buydown Scenario Comparison',
    description: 'Compares different rate buydown options (1-0, 2-1, 3-2-1) to find the best value for your situation.',
  },
  netBenefit: {
    title: 'Net Benefit',
    description: 'Total savings from the buydown minus the cost. Positive = the buydown saves you money.',
    interpretation: 'Higher is better. If negative, the buydown costs more than it saves.',
  },
  breakEvenMonths: {
    title: 'Break-Even Months',
    description: 'How many months until the buydown savings exceed its cost.',
    interpretation: 'If you plan to stay in the home longer than this, the buydown is worthwhile.',
  },
  builderCredit: {
    title: 'Total Builder Credit',
    description: 'The dollar amount the builder/seller is contributing toward closing costs or buydowns.',
  },
  cashToCloseReduction: {
    title: 'Cash-to-Close Reduction',
    description: 'The percentage by which builder credits reduce your out-of-pocket closing costs.',
    interpretation: 'Higher percentage = more of your closing costs are covered by the builder.',
  },

  // Long-Term Tab
  longTermProjection: {
    title: 'Long-Term Financial Projection',
    description: 'Shows your mortgage balance, equity, and costs at 5, 10, 15, 20, 25, and 30-year milestones.',
  },
  netWorthImpact: {
    title: 'Projected Net Worth Impact',
    description: 'How much this home is projected to add to your net worth, accounting for appreciation and equity built.',
    interpretation: 'Positive = the home is building wealth. Factors in assumed appreciation rate.',
  },
  appreciationRate: {
    title: 'Appreciation Rate',
    description: 'The assumed annual increase in home value used for projections.',
    interpretation: 'Historical US average is ~3–4%. Local markets vary. This is an estimate, not a guarantee.',
  },
  rateStrategy: {
    title: 'Rate Strategy Comparison',
    description: 'Compares 15-year, 20-year, and 30-year loan terms to find the optimal balance of monthly payment vs total cost.',
  },

  // Optimization Tab
  optimizationOpportunities: {
    title: 'Optimization Opportunities',
    description: 'AI-identified ways to reduce costs, lower risk, or improve your mortgage terms.',
    interpretation: 'High-priority items should be addressed first. "Easy" difficulty means quick wins.',
  },
  hersIndex: {
    title: 'HERS Index',
    description: 'Home Energy Rating System score. HERS 100 = standard new home. Each point below 100 ≈ 1% more efficient.',
    interpretation: '≤50 = Exceptional, ≤70 = Excellent, ≤85 = Very Good. Lower saves more on utilities.',
  },
  energyEfficiency: {
    title: 'Energy Efficiency Impact',
    description: 'Translates energy efficiency into dollar savings and equivalent home price reduction.',
    interpretation: 'A more efficient home may cost more upfront but saves money monthly on utilities.',
  },

  // Table columns for Long-Term Projection
  totalPaid: {
    title: 'Total Paid',
    description: 'Cumulative amount paid toward the mortgage (principal + interest) up to this milestone.',
  },
  principalPaid: {
    title: 'Principal Paid',
    description: 'Cumulative principal paid — this is equity you\'ve built through payments.',
  },
  interestPaid: {
    title: 'Interest Paid',
    description: 'Cumulative interest paid — the cost of borrowing up to this point.',
  },
  remainingBalance: {
    title: 'Remaining Balance',
    description: 'How much you still owe on the loan at this milestone.',
  },
  homeEquity: {
    title: 'Home Equity',
    description: 'Your ownership stake: Home Value (with appreciation) minus Remaining Balance.',
    interpretation: 'This grows from both paying down the loan and home price appreciation.',
  },

  // === Basic Tab ===
  homePriceBasic: {
    title: 'Home Price',
    description: 'The total purchase price of the property. This is the starting point for all mortgage calculations.',
    interpretation: 'All other values (loan amount, down payment, taxes, insurance) are derived from this number.',
  },
  downPayment: {
    title: 'Down Payment',
    description: 'The upfront cash you pay toward the home. The rest is financed through your mortgage loan.',
    interpretation: '20% or more avoids PMI on conventional loans. Lower down payments mean higher monthly costs.',
  },

  // === Advanced Tab ===
  location: {
    title: 'Property Location',
    description: 'The state and city where the property is located. This affects property tax rates, insurance costs, and available loan programs.',
    interpretation: 'Some states have much higher property taxes (e.g., Texas ~2.4%) vs others (e.g., Hawaii ~0.3%).',
  },
  propertyType: {
    title: 'Property Type',
    description: 'The type of property you\'re buying. This affects insurance rates, HOA likelihood, and some loan program eligibility.',
    interpretation: 'Condos and townhouses often have HOA fees. Multi-family may require higher down payments.',
  },
  creditScore: {
    title: 'Credit Score',
    description: 'A number (300–850) representing your creditworthiness. Lenders use it to determine your interest rate and loan eligibility.',
    interpretation: '740+ gets the best rates. Below 620 limits options significantly. Each 20-point increase can save thousands.',
  },
  propertyTaxRate: {
    title: 'Property Tax Rate',
    description: 'The annual percentage of home value charged as property tax by your local government.',
    interpretation: 'National average is ~1.1%. Rates vary widely by state and county. This is usually your second-largest monthly cost.',
  },
  pidAssessment: {
    title: 'PID Assessment',
    description: 'Public Improvement District assessment — a separate charge from property taxes that funds community infrastructure and amenities.',
    interpretation: 'Common in new construction communities. Typically 0.3%–0.8% of home value annually. Not tax-deductible.',
  },
  annualInsurance: {
    title: 'Annual Insurance',
    description: 'Homeowners insurance premium protecting against damage, theft, and liability. Required by all mortgage lenders.',
    interpretation: 'Typically $1,000–$3,000/year. Varies by location, home value, and coverage level. Shop around for best rates.',
  },
  hoaFees: {
    title: 'Monthly HOA Fee',
    description: 'Homeowners Association fee covering shared amenities, maintenance, and community services.',
    interpretation: 'Common in condos, townhouses, and planned communities. Can range from $50 to $500+/month. Factor this into affordability.',
  },

  // === Closing Costs Tab ===
  loanOrigination: {
    title: 'Loan Origination Fee',
    description: 'Fee charged by the lender to process and underwrite your mortgage application. Typically 0.5%–1% of the loan amount.',
    interpretation: 'This is negotiable. Some lenders waive it in exchange for a slightly higher interest rate.',
  },
  appraisalFee: {
    title: 'Appraisal Fee',
    description: 'Cost of a professional appraisal to determine the home\'s market value. Required by the lender.',
    interpretation: 'Typically $300–$600. If the appraisal comes in low, you may need to renegotiate or increase your down payment.',
  },
  titleServices: {
    title: 'Title Services',
    description: 'Fees for title search, title insurance, and settlement services that ensure the property has a clear ownership history.',
    interpretation: 'Typically $1,000–$3,000. Title insurance protects you and your lender against ownership disputes.',
  },
  governmentRecording: {
    title: 'Government Recording Charges',
    description: 'Fees paid to the county to officially record the property deed and mortgage documents.',
    interpretation: 'Usually $100–$500. These are fixed fees set by local government — not negotiable.',
  },
  prepaids: {
    title: 'Prepaids',
    description: 'Upfront payments for property taxes, homeowners insurance, and prepaid interest due between closing and your first payment.',
    interpretation: 'Typically covers 3–6 months of taxes and 12 months of insurance. This is money you\'d pay anyway — just paid early.',
  },
  initialEscrow: {
    title: 'Initial Escrow Payment',
    description: 'Money deposited into your escrow account at closing to create a reserve for future tax and insurance payments.',
    interpretation: 'Usually 2–3 months of taxes and insurance. Your lender manages this account and pays bills on your behalf.',
  },
  otherClosingCosts: {
    title: 'Other Costs',
    description: 'Miscellaneous closing costs including courier fees, credit report fees, flood certification, and other small charges.',
    interpretation: 'Usually $500–$1,500 total. Ask your lender for a detailed breakdown.',
  },
  vaFundingFee: {
    title: 'VA Funding Fee',
    description: 'A one-time fee charged on VA loans to help fund the program. Can be financed into the loan amount.',
    interpretation: 'Ranges from 1.25% to 3.3% of loan amount. Exempt for veterans with service-connected disabilities.',
  },
  sellerCredit: {
    title: 'Seller Credit',
    description: 'A percentage of the home price that the seller agrees to pay toward your closing costs as part of the deal negotiation.',
    interpretation: 'Typically 2%–6% depending on loan type. Reduces your out-of-pocket costs but doesn\'t reduce the home price.',
  },
  earnestMoney: {
    title: 'Earnest Money Deposit',
    description: 'A good-faith deposit made when your offer is accepted, showing the seller you\'re serious. Applied as a credit at closing.',
    interpretation: 'Typically 1%–3% of purchase price. This isn\'t an extra cost — it\'s subtracted from your cash due at closing.',
  },
  totalClosingCosts: {
    title: 'Total Closing Costs',
    description: 'The sum of all fees and charges due at closing, before any seller credits are applied.',
    interpretation: 'Typically 2%–5% of the home price. Compare your Loan Estimate to ensure you\'re not overpaying.',
  },
  cashDueAtClosing: {
    title: 'Cash Due at Closing',
    description: 'The total amount of cash you need to bring to the closing table: down payment + closing costs − seller credits − earnest money.',
    interpretation: 'This is the most important number for planning your savings. Make sure you have this amount plus a reserve.',
  },
  autoCalculateClosing: {
    title: 'Auto-Calculate',
    description: 'When enabled, closing costs are automatically estimated based on your home price, loan type, and location using industry averages.',
    interpretation: 'Turn this off to enter actual figures from your Loan Estimate for more accurate results.',
  },

  // ── Deal Analyzer ──────────────────────────────────────────────────────
  dealArv: {
    title: 'After Repair Value (ARV)',
    description: 'The estimated market value of a property after all renovations and repairs are completed. Calculated using comparable recently-sold properties in the area.',
    interpretation: 'Higher ARV relative to purchase price + rehab costs = more potential profit.',
  },
  dealCashOnCash: {
    title: 'Cash-on-Cash Return (CoC)',
    description: 'Annual pre-tax cash flow divided by total cash invested (down payment + closing costs + rehab). Measures the return on the actual dollars you put in.',
    interpretation: 'Aim for 8%+ for buy & hold. Higher CoC = your cash is working harder.',
  },
  dealCapRate: {
    title: 'Capitalization Rate (Cap Rate)',
    description: 'Net Operating Income (NOI) divided by the property purchase price. Measures a property\'s yield independent of financing.',
    interpretation: '5–10% is typical. Higher cap rate = higher return but often higher risk.',
  },
  dealEquityCapture: {
    title: 'Equity Capture',
    description: 'The instant equity gained by buying below ARV. Calculated as ARV minus purchase price minus total rehab costs.',
    interpretation: 'Positive equity capture means built-in profit from day one.',
  },
  dealCashflow: {
    title: 'Cash Flow',
    description: 'Monthly rental income minus all expenses: mortgage payment, taxes, insurance, HOA, vacancy, property management, and maintenance.',
    interpretation: 'Positive cash flow means the property pays for itself and generates income.',
  },
  dealRehabRisk: {
    title: 'Rehab Risk Score',
    description: 'A 0–100 score estimating how risky and unpredictable the renovation will be, based on property age, condition, and scope of work.',
    interpretation: 'Lower = safer. High scores mean potential cost overruns and timeline delays.',
  },
  dealMarketStrength: {
    title: 'Market Strength Score',
    description: 'A 0–100 score reflecting local market health: demand, days on market, price trends, rental demand, and economic indicators.',
    interpretation: 'Higher = stronger market. Strong markets support better ARV and faster sales/rentals.',
  },
  dealNoi: {
    title: 'Net Operating Income (NOI)',
    description: 'Annual rental income minus operating expenses (taxes, insurance, HOA, maintenance, management, vacancy). Does NOT include mortgage payments.',
    interpretation: 'NOI is used to calculate cap rate and evaluate a deal independent of financing.',
  },
  dealMao: {
    title: 'Maximum Allowable Offer (MAO)',
    description: 'The highest price an investor should pay. Typically calculated as ARV × 70% minus rehab costs, ensuring enough margin for profit.',
    interpretation: 'Buying at or below MAO gives you a built-in safety margin.',
  },
  dealMonthlyPI: {
    title: 'Monthly P&I (Principal & Interest)',
    description: 'The monthly loan payment covering principal repayment and interest charges, excluding taxes and insurance.',
    interpretation: 'This is your base mortgage cost before adding taxes, insurance, and HOA.',
  },
  dealVacancyRate: {
    title: 'Vacancy Rate',
    description: 'The estimated percentage of time the property will be unoccupied. Typical range is 3–8% depending on the market.',
    interpretation: 'Lower vacancy = more consistent income. Factor in turnover costs too.',
  },
  dealPropertyManagement: {
    title: 'Property Management Rate',
    description: 'The percentage of monthly rent paid to a property manager to handle tenants, maintenance, and day-to-day operations.',
    interpretation: 'Typically 8–12%. Even self-managing investors should account for this cost.',
  },
  dealPropertyTaxRate: {
    title: 'Effective Property Tax Rate',
    description: 'The combined annual tax rate from all taxing authorities (county, city, school district, special districts) applied to the property\'s assessed value.',
    interpretation: 'Varies widely by location. Texas averages ~2%, while some states are under 1%.',
  },
  dealHoaBreakdown: {
    title: 'HOA Fee Breakdown',
    description: 'Homeowners Association fees broken down by what they cover: landscaping, pool, insurance, management, reserves, and other community services.',
    interpretation: 'Understand what\'s included — some HOAs cover water/trash, which reduces your other expenses.',
  },
  dealFlipROI: {
    title: 'Flip ROI (Return on Investment)',
    description: 'Projected profit from flipping divided by total investment (purchase + rehab + holding costs + closing costs).',
    interpretation: 'Aim for 15%+ ROI on flips to account for unexpected costs and market shifts.',
  },
  dealEquitySpread: {
    title: 'Equity Spread',
    description: 'The difference between the adjusted ARV and your purchase price, expressed in dollars and as a percentage of ARV.',
    interpretation: 'A healthy spread (25%+) provides a safety cushion against market dips.',
  },
};
